import { useEffect, useState, type ReactNode } from 'react';
import { Panel } from 'react-resizable-panels';

import { entityName, type EntityDesc } from '../../lib/scene-doc.js';
import { useScene } from '../../state/scene.js';
import type { FieldDef } from '../scene/component-types.js';
import { CurveField } from '../scene/fields/CurveField.js';
import { GradientField } from '../scene/fields/GradientField.js';
import particlesForm from '../scene/forms/particles.js';
import { FieldEditor } from '../scene/Inspector.js';
import { EmptyState, IconButton, MenuButton, NumberField, PropRow, SelectField, Toggle } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { Handle, SplitGroup } from '../ui/layout.js';
import {
  INHERIT_OPTIONS,
  MODULE_BY_KEY,
  NEW_PARTICLE_SYSTEM,
  PARTICLE_MODULES,
  SUB_EMITTER_TYPES,
  addBurst,
  addSubEmitter,
  bursts,
  isModuleEnabled,
  moduleSummary,
  moduleValue,
  newSubEmitterCommands,
  particleCommand,
  removeBurst,
  removeSubEmitter,
  resetModule,
  setModuleEnabled,
  subEmitterCandidates,
  subEmitters,
  updateBurst,
  updateSubEmitter,
  type ModuleDef,
  type ParticleSystem,
  type SubEmitterType,
} from './particle-model.js';
import { ParticlePreviewView } from './ParticlePreviewView.js';
import { PlaybackBar } from './PlaybackBar.js';
import { loadPrefs, savePrefs } from './prefs.js';
import { activeReport, beginEditGesture, endEditGesture, particleTarget, useParticlePreview } from './preview-store.js';
import './particles.css';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Field definitions per module, shared with the Inspector's form (scene/forms/particles.ts). */
const MODULE_FIELDS: ReadonlyMap<string, FieldDef[]> = new Map(
  particlesForm.fields.map((f) => [f.key, f.type.kind === 'object' ? f.type.fields.filter((x) => x.key !== 'enabled') : []]),
);

/** Fields the editor draws with its own controls. */
const CUSTOM_FIELDS = new Set(['emission.bursts', 'collision.planes', 'subEmitters.emitters', 'textureSheetAnimation.tiles']);

/**
 * Particle Editor (Unity's Particle System editor) for the selected entity's
 * `particleSystem`, as a dockable tool panel: playback controls, a live
 * isolated preview, and the modules as collapsible cards with enable
 * checkboxes, curve and gradient editors. Every edit goes through the scene
 * store (`setComponent`), and a drag is one undo step.
 */
export function ParticleEditor() {
  const doc = useScene((s) => s.doc);
  useScene((s) => s.selection);
  const locked = useParticlePreview((s) => s.locked);
  const setLocked = useParticlePreview((s) => s.setLocked);
  const [prefs, setPrefs] = useState(loadPrefs);
  const target = particleTarget(locked);
  const entity = target && doc ? doc.entities.find((e) => e.id === target) : undefined;
  const ps = entity && isObj(entity.components?.particleSystem) ? (entity.components!.particleSystem as ParticleSystem) : undefined;

  // A drag anywhere in the panel is one undo step.
  useEffect(() => {
    const up = () => endEditGesture();
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    return () => {
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
    };
  }, []);

  // A lock on an entity that is gone is dropped.
  useEffect(() => {
    if (locked && doc && !doc.entities.some((e) => e.id === locked)) setLocked(null);
  }, [doc, locked, setLocked]);

  const updatePrefs = (next: typeof prefs) => {
    setPrefs(next);
    savePrefs(next);
  };

  if (!doc) {
    return (
      <div className="pe">
        <EmptyState icon="particles" title="No scene open">
          Open a scene to edit its particle systems.
        </EmptyState>
      </div>
    );
  }
  if (!entity || !ps) return <NoTarget />;

  const update = (next: ParticleSystem) => useScene.getState().apply(particleCommand(entity.id, next));
  const systems = doc.entities.filter((e) => e.components && 'particleSystem' in e.components);

  return (
    <div className="pe" onPointerDownCapture={beginEditGesture}>
      <div className="pe-head">
        <Icon name="particles" size={14} />
        <div className="pe-target">
          <SelectField
            value={entity.id}
            options={systems.map((e) => ({ value: e.id, label: entityName(e) }))}
            onChange={(id) => {
              if (locked) setLocked(id);
              else useScene.getState().select([id]);
            }}
          />
        </div>
        <IconButton
          icon={locked ? 'target' : 'cursor'}
          title={locked ? 'Locked to this system (click to follow the selection)' : 'Following the selection (click to lock to this system)'}
          active={Boolean(locked)}
          onClick={() => setLocked(locked ? null : entity.id)}
        />
        <IconButton
          icon={prefs.showPreview ? 'eye' : 'eyeOff'}
          title={prefs.showPreview ? 'Hide the preview' : 'Show the preview'}
          onClick={() => updatePrefs({ ...prefs, showPreview: !prefs.showPreview })}
        />
        <MenuButton
          className="ibtn"
          title="Particle system"
          items={[
            { id: 'expand', label: 'Expand all modules', icon: 'chevronDown', run: () => updatePrefs({ ...prefs, collapsed: [] }) },
            { id: 'collapse', label: 'Collapse all modules', icon: 'chevronRight', run: () => updatePrefs({ ...prefs, collapsed: PARTICLE_MODULES.map((m) => m.key) }) },
            { id: 'sep', label: '', separator: true },
            { id: 'copy', label: 'Copy JSON', icon: 'copy', run: () => void navigator.clipboard?.writeText(JSON.stringify(ps, null, 2)) },
            { id: 'reset', label: 'Reset system', icon: 'restart', danger: true, run: () => update(JSON.parse(JSON.stringify(NEW_PARTICLE_SYSTEM)) as ParticleSystem) },
          ]}
        >
          <Icon name="more" size={14} />
        </MenuButton>
      </div>
      <PlaybackBar />
      {prefs.showPreview ? (
        <SplitGroup id="particle-editor" orientation="vertical" fallback={{ 'pe-preview': 38, 'pe-modules': 62 }} className="pe-split">
          <Panel id="pe-preview" minSize={90} className="pe-preview-pane">
            <ParticlePreviewView target={entity.id} />
          </Panel>
          <Handle orientation="horizontal" />
          <Panel id="pe-modules" minSize={120} className="pe-modules-pane">
            <Modules entity={entity} ps={ps} update={update} prefs={prefs} setPrefs={updatePrefs} />
          </Panel>
        </SplitGroup>
      ) : (
        <Modules entity={entity} ps={ps} update={update} prefs={prefs} setPrefs={updatePrefs} />
      )}
    </div>
  );
}

function NoTarget() {
  const doc = useScene((s) => s.doc);
  const selection = useScene((s) => s.selection);
  const active = doc?.entities.find((e) => e.id === selection.at(-1));
  return (
    <div className="pe">
      <EmptyState icon="particles" title="No particle system selected">
        <p className="muted">Select an entity with a Particle System, or:</p>
        <div className="pe-empty-actions">
          {active && (
            <button
              type="button"
              className="ghost small"
              onClick={() =>
                useScene.getState().apply({ cmd: 'setComponent', id: active.id, key: 'particleSystem', value: JSON.parse(JSON.stringify(NEW_PARTICLE_SYSTEM)) })
              }
            >
              <Icon name="plus" size={12} /> Add a Particle System to {entityName(active)}
            </button>
          )}
          <button
            type="button"
            className="ghost small"
            onClick={() =>
              useScene.getState().apply({
                cmd: 'addEntity',
                desc: { id: '', name: 'Particle System', transform: { position: [0, 1, 0] }, components: { particleSystem: JSON.parse(JSON.stringify(NEW_PARTICLE_SYSTEM)) } },
              })
            }
          >
            <Icon name="particles" size={12} /> Create a Particle System
          </button>
        </div>
      </EmptyState>
    </div>
  );
}

interface ModulesProps {
  entity: EntityDesc;
  ps: ParticleSystem;
  update: (next: ParticleSystem) => void;
  prefs: { collapsed: string[]; showPreview: boolean };
  setPrefs: (prefs: { collapsed: string[]; showPreview: boolean }) => void;
}

function Modules({ entity, ps, update, prefs, setPrefs }: ModulesProps) {
  const reports = useParticlePreview((s) => s.reports);
  const report = activeReport(reports);
  const system = report?.systems.find((s) => s.id === entity.id) ?? report?.systems[0];
  const cycle = system && system.duration > 0 ? (system.time % system.duration) / system.duration : null;
  return (
    <div className="pe-modules">
      {PARTICLE_MODULES.map((def) => {
        const open = !prefs.collapsed.includes(def.key);
        return (
          <ModuleCard
            key={def.key}
            def={def}
            ps={ps}
            update={update}
            open={open}
            onToggleOpen={() =>
              setPrefs({ ...prefs, collapsed: open ? [...prefs.collapsed, def.key] : prefs.collapsed.filter((k) => k !== def.key) })
            }
          >
            {open && <ModuleBody def={def} entity={entity} ps={ps} update={update} cycle={cycle} />}
          </ModuleCard>
        );
      })}
    </div>
  );
}

function ModuleCard({
  def,
  ps,
  update,
  open,
  onToggleOpen,
  children,
}: {
  def: ModuleDef;
  ps: ParticleSystem;
  update: (next: ParticleSystem) => void;
  open: boolean;
  onToggleOpen: () => void;
  children: ReactNode;
}) {
  const enabled = isModuleEnabled(ps, def.key);
  const present = moduleValue(ps, def.key) !== undefined;
  const summary = enabled ? moduleSummary(ps, def.key) : '';
  return (
    <section className={`pe-module${enabled ? '' : ' is-off'}${open ? ' is-open' : ''}`} aria-label={def.label}>
      <header className="pe-module-head">
        <button type="button" className="pe-module-toggle" onClick={onToggleOpen} aria-expanded={open} title={def.hint}>
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
        </button>
        {def.toggle ? (
          <input
            type="checkbox"
            className="pe-module-check"
            checked={enabled}
            title={enabled ? `Disable ${def.label}` : `Enable ${def.label}`}
            aria-label={`${def.label} enabled`}
            onChange={(e) => update(setModuleEnabled(ps, def.key, e.target.checked))}
          />
        ) : (
          <span className="pe-module-check-space" />
        )}
        <button type="button" className="pe-module-title" onClick={onToggleOpen} title={def.hint}>
          <Icon name={def.icon} size={13} />
          <span>{def.label}</span>
          {summary && <span className="pe-module-summary">{summary}</span>}
        </button>
        <MenuButton
          className="ibtn"
          title={`${def.label} options`}
          items={[
            { id: 'reset', label: 'Reset module', icon: 'restart', run: () => update(resetModule(ps, def.key)) },
            ...(def.optional
              ? [{ id: 'remove', label: 'Remove module', icon: 'trash' as const, danger: true, disabled: !present, run: () => update(resetModule(ps, def.key, true)) }]
              : []),
          ]}
        >
          <Icon name="more" size={13} />
        </MenuButton>
      </header>
      {children && <div className="pe-module-body">{children}</div>}
    </section>
  );
}

/** Module fields: the shared form's fields plus custom editors (bursts, planes, sub-emitters, tiles). */
function ModuleBody({ def, entity, ps, update, cycle }: { def: ModuleDef; entity: EntityDesc; ps: ParticleSystem; update: (next: ParticleSystem) => void; cycle: number | null }) {
  const current = moduleValue(ps, def.key);
  // Editing a module that is off and absent stores it disabled (like Unity: editing does not enable).
  const base: Obj = current ?? (def.optional ? { ...JSON.parse(JSON.stringify(def.defaults)), enabled: false } : {});
  const setModule = (next: Obj) => update({ ...ps, [def.key]: next });
  const setField = (key: string, v: unknown) => {
    const next = { ...base };
    if (v === undefined) delete next[key];
    else next[key] = v;
    setModule(next);
  };
  const timeKind = def.key === 'main' || def.key === 'emission' ? 'cycle' : 'lifetime';
  const fields = (MODULE_FIELDS.get(def.key) ?? []).filter((f) => !f.showIf || f.showIf(base));

  return (
    <>
      {fields.map((f) => {
        const path = `${def.key}.${f.key}`;
        if (CUSTOM_FIELDS.has(path)) {
          return (
            <PropRow key={f.key} label={f.label} hint={f.hint ?? f.key}>
              <CustomField path={path} entity={entity} ps={ps} base={base} update={update} setField={setField} />
            </PropRow>
          );
        }
        return (
          <PropRow key={f.key} label={f.label} hint={f.hint ?? f.key}>
            <ModuleField def={f} value={base[f.key]} onChange={(v) => setField(f.key, v)} timeKind={timeKind} playhead={timeKind === 'cycle' ? cycle : null} />
          </PropRow>
        );
      })}
      {MODULE_BY_KEY.get(def.key)?.optional && !current && <p className="pe-note muted">{def.hint} Enable the module to use it.</p>}
    </>
  );
}

function ModuleField({
  def,
  value,
  onChange,
  timeKind,
  playhead,
}: {
  def: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  timeKind: 'cycle' | 'lifetime';
  playhead: number | null;
}) {
  const t = def.type;
  if (t.kind === 'curve') {
    return (
      <CurveField
        value={value}
        onChange={onChange}
        step={t.step}
        min={t.min}
        max={t.max}
        modes={t.modes}
        plain={t.plain}
        extras={{ playhead, timeLabel: timeKind === 'cycle' ? 'cycle' : 'lifetime', height: 110 }}
      />
    );
  }
  if (t.kind === 'gradient') return <GradientField value={value} onChange={onChange} modes={t.modes} plain={t.plain} extras={{ playhead }} />;
  return <FieldEditor def={def} value={value} onChange={(v) => onChange(v)} />;
}

function CustomField({
  path,
  entity,
  ps,
  base,
  update,
  setField,
}: {
  path: string;
  entity: EntityDesc;
  ps: ParticleSystem;
  base: Obj;
  update: (next: ParticleSystem) => void;
  setField: (key: string, v: unknown) => void;
}) {
  switch (path) {
    case 'emission.bursts':
      return <BurstList ps={ps} update={update} />;
    case 'collision.planes':
      return <EntityList entity={entity} value={Array.isArray(base.planes) ? (base.planes as unknown[]).filter((p): p is string => typeof p === 'string') : []} onChange={(v) => setField('planes', v)} />;
    case 'subEmitters.emitters':
      return <SubEmitterList entity={entity} ps={ps} update={update} />;
    case 'textureSheetAnimation.tiles': {
      const tiles = Array.isArray(base.tiles) ? (base.tiles as number[]) : [1, 1];
      return (
        <div className="pe-pair">
          <NumberField label="X" title="Columns" value={tiles[0] ?? 1} step={1} min={1} integer onChange={(x) => setField('tiles', [x, tiles[1] ?? 1])} />
          <NumberField label="Y" title="Rows" value={tiles[1] ?? 1} step={1} min={1} integer onChange={(y) => setField('tiles', [tiles[0] ?? 1, y])} />
        </div>
      );
    }
    default:
      return null;
  }
}

/** Burst count: a constant or random between two constants (curves stay as they are). */
function CountField({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  if (Array.isArray(value) && value.length === 2) {
    const [a, b] = value as [number, number];
    return (
      <div className="pe-pair">
        <NumberField label="↓" title="Min count" value={a} step={1} min={0} integer onChange={(v) => onChange([v, b])} />
        <NumberField label="↑" title="Max count" value={b} step={1} min={0} integer onChange={(v) => onChange([a, v])} />
        <IconButton icon="minus" title="Constant count" size={11} onClick={() => onChange(Math.round((a + b) / 2))} />
      </div>
    );
  }
  if (typeof value === 'number' || value === undefined) {
    const n = typeof value === 'number' ? value : 30;
    return (
      <div className="pe-pair">
        <NumberField value={n} step={1} min={0} integer onChange={onChange} />
        <IconButton icon="plus" title="Random between two counts" size={11} onClick={() => onChange([Math.max(0, n - 5), n + 5])} />
      </div>
    );
  }
  return <span className="muted">curve</span>;
}

function BurstList({ ps, update }: { ps: ParticleSystem; update: (next: ParticleSystem) => void }) {
  const list = bursts(ps);
  return (
    <div className="pe-list">
      {list.length > 0 && (
        <div className="pe-burst pe-list-head">
          <span>Time</span>
          <span>Count</span>
          <span title="Times it fires (0 = until the cycle ends)">Cycles</span>
          <span>Interval</span>
          <span title="Chance each firing happens (0..1)">Prob.</span>
          <span />
        </div>
      )}
      {list.map((b, i) => (
        <div key={i} className="pe-burst">
          <NumberField value={b.time ?? 0} step={0.05} min={0} onChange={(v) => update(updateBurst(ps, i, { time: v }))} />
          <CountField value={b.count} onChange={(v) => update(updateBurst(ps, i, { count: v }))} />
          <NumberField value={b.cycles ?? 1} step={1} min={0} integer onChange={(v) => update(updateBurst(ps, i, { cycles: v }))} />
          <NumberField value={b.interval ?? 0.01} step={0.01} min={0.001} onChange={(v) => update(updateBurst(ps, i, { interval: v }))} />
          <NumberField value={b.probability ?? 1} step={0.05} min={0} max={1} onChange={(v) => update(updateBurst(ps, i, { probability: v }))} />
          <IconButton icon="trash" title="Remove burst" size={12} onClick={() => update(removeBurst(ps, i))} />
        </div>
      ))}
      <button type="button" className="ghost small pe-add" onClick={() => update(addBurst(ps))}>
        <Icon name="plus" size={12} /> Add burst
      </button>
    </div>
  );
}

/** A list of entity ids (collision planes) with an "add" picker. */
function EntityList({ entity, value, onChange }: { entity: EntityDesc; value: string[]; onChange: (v: string[]) => void }) {
  const doc = useScene((s) => s.doc);
  const options = (doc?.entities ?? []).filter((e) => e.id !== entity.id && !value.includes(e.id));
  const name = (id: string) => {
    const e = doc?.entities.find((x) => x.id === id);
    return e ? entityName(e) : `${id} (missing)`;
  };
  return (
    <div className="pe-list">
      {value.map((id) => (
        <div key={id} className="pe-chip-row">
          <span className="pe-chip" title={id}>
            {name(id)}
          </span>
          <IconButton icon="x" title="Remove plane" size={11} onClick={() => onChange(value.filter((x) => x !== id))} />
        </div>
      ))}
      <SelectField
        value={undefined}
        placeholder="Add plane entity…"
        options={options.map((e) => ({ value: e.id, label: entityName(e) }))}
        onChange={(id) => id && onChange([...value, id])}
      />
    </div>
  );
}

function SubEmitterList({ entity, ps, update }: { entity: EntityDesc; ps: ParticleSystem; update: (next: ParticleSystem) => void }) {
  const doc = useScene((s) => s.doc);
  const list = subEmitters(ps);
  const candidates = doc ? subEmitterCandidates(doc, entity.id) : [];
  const create = (type: SubEmitterType) => {
    if (!doc) return;
    const before = useScene.getState().selection;
    const { commands } = newSubEmitterCommands(doc, entity.id, ps, type);
    useScene.getState().applyBatch(commands);
    // Keep editing the parent (adding entities selects them).
    useScene.getState().select(before);
  };
  return (
    <div className="pe-list">
      {list.map((s, i) => (
        <div key={i} className="pe-sub">
          <div className="pe-sub-row">
            <SelectField value={s.type ?? 'birth'} options={SUB_EMITTER_TYPES as readonly string[]} onChange={(v) => update(updateSubEmitter(ps, i, { type: v as SubEmitterType }))} />
            <SelectField
              value={s.emitter || undefined}
              placeholder="Emitter…"
              options={candidates.map((e) => ({ value: e.id, label: entityName(e) }))}
              onChange={(v) => update(updateSubEmitter(ps, i, { emitter: v }))}
            />
            <IconButton icon="trash" title="Remove sub-emitter" size={12} onClick={() => update(removeSubEmitter(ps, i))} />
          </div>
          <div className="pe-sub-row">
            <NumberField label="Count" title="Particles per event (0 = the child's bursts)" value={s.count ?? 0} step={1} min={0} integer onChange={(v) => update(updateSubEmitter(ps, i, { count: v || undefined }))} />
            <NumberField label="Prob." title="Chance per event (0..1)" value={s.probability ?? 1} step={0.05} min={0} max={1} onChange={(v) => update(updateSubEmitter(ps, i, { probability: v }))} />
          </div>
          <div className="pe-sub-row pe-inherit" title="Properties the child particles take from the parent particle">
            {INHERIT_OPTIONS.map((k) => (
              <Toggle
                key={k}
                label={k}
                checked={(s.inherit ?? []).includes(k)}
                onChange={(on) => update(updateSubEmitter(ps, i, { inherit: on ? [...(s.inherit ?? []), k] : (s.inherit ?? []).filter((x) => x !== k) }))}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="pe-sub-row">
        <MenuButton
          className="ghost small pe-add"
          title="Create a child particle system that emits on an event"
          items={SUB_EMITTER_TYPES.map((t) => ({ id: t, label: `New ${t} emitter`, icon: 'plus' as const, run: () => create(t) }))}
        >
          <Icon name="plus" size={12} /> New sub-emitter
        </MenuButton>
        {candidates.length > 0 && (
          <SelectField
            value={undefined}
            placeholder="Link existing…"
            options={candidates.map((e) => ({ value: e.id, label: entityName(e) }))}
            onChange={(id) => id && update(addSubEmitter(ps, { type: 'death', emitter: id }))}
          />
        )}
      </div>
    </div>
  );
}
